import type {
  CheckoutBrandingResponse,
  CheckoutBrandingUpdate,
  ShopCreateRequest,
  ShopResponse,
  ShopUpdateRequest,
  GatewayConnectRequest,
  GatewayResponse,
  ProfileLogoUploadResponse,
  ShopStatsResponse,
  StatsPeriod,
} from '../types';
import type { RequestExtras, RequestFn } from '../client';

/** Manage gateway connections for a specific shop. */
class ShopGateways {
  constructor(private readonly request: RequestFn) {}

  /**
   * Connect a payment gateway to a shop.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop (business profile) ID.
   * @param params - Gateway connector credentials and configuration.
   * @returns The created gateway connection.
   */
  async connect(
    merchantId: string,
    shopId: string,
    params: GatewayConnectRequest,
  ): Promise<GatewayResponse> {
    return this.request(
      'POST',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/gateways`,
      { body: params },
    );
  }

  /**
   * List all gateway connections for a shop.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop ID.
   * @returns Array of gateway connections.
   */
  async list(merchantId: string, shopId: string): Promise<GatewayResponse[]> {
    return this.request(
      'GET',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/gateways`,
    );
  }

  /**
   * Disconnect a gateway from a shop.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop ID.
   * @param gatewayId - The gateway connector ID to remove.
   * @returns The removed gateway connection.
   */
  async disconnect(
    merchantId: string,
    shopId: string,
    gatewayId: string,
  ): Promise<GatewayResponse> {
    return this.request(
      'DELETE',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/gateways/${encodeURIComponent(gatewayId)}`,
    );
  }
}

/**
 * Create and manage shops (business profiles) within a merchant account.
 *
 * Each shop can have its own gateway connections, routing rules, and fee schedules.
 */
export class Shops {
  /** Gateway connection management for shops. */
  readonly gateways: ShopGateways;

  constructor(private readonly request: RequestFn) {
    this.gateways = new ShopGateways(request);
  }

  /**
   * Create a new shop under a merchant account.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Shop creation parameters (name, etc.).
   * @returns The created shop.
   *
   * @example
   * ```typescript
   * const shop = await delopay.shops.create('merch_123', { shop_name: 'EU Store' });
   * ```
   */
  async create(merchantId: string, params: ShopCreateRequest): Promise<ShopResponse> {
    return this.request('POST', `/shops/${encodeURIComponent(merchantId)}`, { body: params });
  }

  /**
   * Retrieve a shop by its ID.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop ID.
   * @returns The shop.
   */
  async retrieve(merchantId: string, shopId: string): Promise<ShopResponse> {
    return this.request(
      'GET',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}`,
    );
  }

  /**
   * Update a shop's configuration.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop ID to update.
   * @param params - Fields to update.
   * @returns The updated shop.
   */
  async update(
    merchantId: string,
    shopId: string,
    params: ShopUpdateRequest,
  ): Promise<ShopResponse> {
    return this.request(
      'PUT',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}`,
      { body: params },
    );
  }

  /**
   * Delete a shop.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop ID to delete.
   * @returns The deleted shop object.
   */
  async delete(merchantId: string, shopId: string): Promise<ShopResponse> {
    return this.request(
      'DELETE',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}`,
    );
  }

  /**
   * List all shops under a merchant account.
   *
   * @param merchantId - The merchant account ID.
   * @returns Array of shops.
   */
  async list(merchantId: string): Promise<ShopResponse[]> {
    return this.request('GET', `/shops/${encodeURIComponent(merchantId)}`);
  }

  /**
   * Successful-order count and revenue for one shop.
   *
   * Unlike `projects.stats()` this needs only `ProfileAccountRead`, so a
   * shop-scoped user can load it for their own shop; merchant-level users can
   * load any shop of their merchant.
   *
   * Revenue comes back FX-converted as `revenue_usd` (USD major units) plus a
   * `revenue_by_currency` breakdown. The legacy `revenue` field is a raw
   * cross-currency minor-unit sum and should not be displayed.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop (business profile) ID.
   * @param period - Window in days, or `'all'` for an all-time total.
   *   Omitted means the server default of 30 days.
   * @returns The shop's stats over the requested window.
   *
   * @example
   * ```typescript
   * const stats = await delopay.shops.stats('merch_123', 'pro_1', 'all');
   * console.log(stats.orders, stats.revenue_usd);
   * ```
   */
  async stats(
    merchantId: string,
    shopId: string,
    period?: StatsPeriod,
  ): Promise<ShopStatsResponse> {
    const path = `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/stats`;
    if (period === undefined) return this.request('GET', path);
    return this.request('GET', path, { query: { period: String(period) } });
  }

  /**
   * Upload a logo file for a shop. The file is stored in Delopay's configured
   * object store and a public HTTPS URL is returned. This method does NOT write
   * the URL into the shop's `payment_link_config.logo` — call
   * `shops.update` afterwards with the returned `logo_url` to persist the change.
   *
   * Accepts PNG, JPEG, WebP or SVG. The file must be ≤ 1 MiB.
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop (business profile) ID.
   * @param file - The logo file (Blob / File in browsers).
   * @returns The publicly-reachable URL of the uploaded logo.
   *
   * @example
   * ```typescript
   * const { logo_url } = await delopay.shops.uploadLogo('merch_1', 'pro_1', file);
   * await delopay.shops.update('merch_1', 'pro_1', {
   *   payment_link_config: { logo: logo_url },
   * });
   * ```
   */
  async uploadLogo(
    merchantId: string,
    shopId: string,
    file: Blob,
  ): Promise<ProfileLogoUploadResponse> {
    const form = new FormData();
    form.append('file', file);
    return this.request(
      'POST',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/logo`,
      { body: form },
    );
  }

  /**
   * Update only the checkout appearance (the `payment_link_config` blob:
   * theme, logo, colours, seller name, SDK layout/rules, DeloPay-branding
   * toggle) of a shop. Applied as a whole-object replace of
   * `payment_link_config`, mirroring the shop-update semantics.
   *
   * Gated on the dedicated `CheckoutBranding` permission, so "may restyle
   * the checkout" can be granted without full account/shop write.
   *
   * `POST /shops/{merchantId}/{shopId}/checkout-branding`
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop (business profile) ID to restyle.
   * @param params - The new `payment_link_config` blob (full replacement).
   * @returns The saved branding — the same shape `retrieveCheckoutBranding`
   * returns, not the whole shop. A role whose only power is restyling a
   * checkout must not receive the webhook signing key or the card-vault
   * configuration back from a save.
   */
  async updateCheckoutBranding(
    merchantId: string,
    shopId: string,
    params: CheckoutBrandingUpdate,
    options?: RequestExtras,
  ): Promise<CheckoutBrandingResponse> {
    return this.request(
      'POST',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/checkout-branding`,
      { body: params, ...options },
    );
  }

  /**
   * Read only the checkout appearance of a shop, so the role that may restyle a
   * checkout can load the checkout it may restyle.
   *
   * Gated on `ProfileCheckoutBrandingRead` — the read twin of the
   * `ProfileCheckoutBrandingEdit` guard on `updateCheckoutBranding` above.
   * Prefer this over `shops.retrieve` for the branding editor: `retrieve`
   * returns the whole profile, including the webhook signing key and the
   * card-vault configuration, and needs the shop-read permission for exactly
   * that reason.
   *
   * `GET /shops/{merchantId}/{shopId}/checkout-branding`
   *
   * @param merchantId - The merchant account ID.
   * @param shopId - The shop (business profile) ID whose branding to read.
   * @returns The shop's id, name and stored `payment_link_config`. That config
   * is `null` when the shop has never been styled — the untouched default, not
   * an error, and distinct from a stored-but-empty style.
   */
  async retrieveCheckoutBranding(
    merchantId: string,
    shopId: string,
    options?: RequestExtras,
  ): Promise<CheckoutBrandingResponse> {
    return this.request(
      'GET',
      `/shops/${encodeURIComponent(merchantId)}/${encodeURIComponent(shopId)}/checkout-branding`,
      options,
    );
  }
}
